import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useUiPrefs } from "../hooks/useUiPrefs";
import { Link } from "wouter";
import { addDoc, collection, deleteDoc, doc, limit, onSnapshot, orderBy, query, updateDoc, where } from "firebase/firestore";
import { Trash2 } from "lucide-react";
import { paths, ROLE_RANK, type Todo } from "@clevis/shared-types";
import { Button, Card, CardTitle, Input, Select, cn } from "@clevis/shared-ui";
import { db } from "../lib/firebase";
import { useAuth } from "../hooks/useAuth";
import { useTeamMembers } from "../hooks/useSalesSettings";

// To-dos: small follow-ups for someone, optionally due, optionally about a
// job, customer or project. On the dashboard (mine / ones I gave out /
// everyone's for managers) and on the record they're about.

type Row = Todo & { id: string };
const DAY = 86_400_000;
const todayStart = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};
const dueLabel = (ms: number) => {
  const t = todayStart();
  if (ms < t) return `overdue ${new Date(ms).toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;
  if (ms < t + DAY) return "today";
  if (ms < t + 2 * DAY) return "tomorrow";
  return new Date(ms).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
};
const linkHref = (l: NonNullable<Todo["link"]>) => (l.kind === "job" ? `/jobs/${l.id}` : l.kind === "customer" ? `/customers/${l.id}` : `/projects/${l.id}`);

export function TodoCard({ tenantId, link = null, title = "To-dos" }: { tenantId: string; link?: Todo["link"]; title?: string }) {
  const { user, role } = useAuth();
  const team = useTeamMembers(tenantId);
  const isManager = !!role && ROLE_RANK[role] >= ROLE_RANK.manager;
  const [rows, setRows] = useState<Row[] | null>(null);
  const [view, setView] = useState<"mine" | "given" | "all">(link ? "all" : "mine");
  const [showDone, setShowDone] = useState(false);
  const [text, setText] = useState("");
  const [who, setWho] = useState("");
  const [due, setDue] = useState("");
  const [error, setError] = useState("");
  const { prefs, save } = useUiPrefs();

  useEffect(() => {
    const col = collection(db, paths.tenantTodos(tenantId));
    const q = link ? query(col, where("link.id", "==", link.id), limit(100)) : query(col, orderBy("createdAt", "desc"), limit(300));
    return onSnapshot(q, (s) => setRows(s.docs.map((d) => ({ id: d.id, ...(d.data() as Todo) }))), () => setRows([]));
  }, [tenantId, link?.id]);

  const me = user?.uid ?? "";
  const shown = useMemo(() => {
    const list = (rows ?? [])
      .filter((r) => (link ? true : view === "mine" ? r.assignedUid === me : view === "given" ? r.createdByUid === me && r.assignedUid !== me : true))
      .filter((r) => showDone || !r.done);
    // Open first (due soonest, undated last), then done (newest first).
    return list.sort((a, b) => Number(a.done) - Number(b.done) || (a.done ? (b.doneAt ?? 0) - (a.doneAt ?? 0) : (a.dueAt ?? Infinity) - (b.dueAt ?? Infinity) || a.createdAt - b.createdAt));
  }, [rows, view, showDone, me, link]);
  const nameOf = (uid: string) => team.find((m) => m.uid === uid)?.name ?? "Teammate";

  async function add(e: FormEvent) {
    e.preventDefault();
    if (!text.trim() || !me) return;
    setError("");
    const assignee = who || me;
    const todo: Todo = {
      title: text.trim().slice(0, 300),
      note: "",
      dueAt: due ? new Date(`${due}T12:00:00`).getTime() : null,
      assignedUid: assignee,
      assignedName: nameOf(assignee),
      createdByUid: me,
      createdByName: nameOf(me),
      createdAt: Date.now(),
      done: false,
      doneAt: null,
      link,
    };
    try {
      await addDoc(collection(db, paths.tenantTodos(tenantId)), todo);
      setText("");
      setDue("");
    } catch (err) {
      setError((err as Error).message);
    }
  }
  const toggle = (r: Row) => void updateDoc(doc(db, paths.tenantTodo(tenantId, r.id)), { done: !r.done, doneAt: r.done ? null : Date.now() }).catch((err) => setError((err as Error).message));
  const remove = (r: Row) => void deleteDoc(doc(db, paths.tenantTodo(tenantId, r.id))).catch((err) => setError((err as Error).message));
  if (rows === null) return null;
  const openCount = (rows ?? []).filter((r) => !r.done && (link || r.assignedUid === me)).length;

  return (
    <Card className="p-0">
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 pt-3">
        <CardTitle className="mb-0">
          {title}
          {openCount ? <span className="ml-1 text-xs font-normal text-steel">({openCount} open{link ? "" : " for you"})</span> : null}
        </CardTitle>
        <div className="flex items-center gap-2 text-xs">
          {!link ? (
            <Select className="w-36 py-0.5 text-xs" value={view} aria-label="Whose to-dos" onChange={(e) => setView(e.target.value as typeof view)}>
              <option value="mine">Mine</option>
              <option value="given">I gave out</option>
              {isManager ? <option value="all">Everyone's</option> : null}
            </Select>
          ) : null}
          <label className="flex items-center gap-1 text-steel">
            <input type="checkbox" checked={showDone} onChange={(e) => setShowDone(e.target.checked)} /> done
          </label>
          {!link && prefs.todoDock === "hidden" ? (
            <button className="text-steel underline hover:text-ink" onClick={() => save({ todoDock: "shown" })}>
              Show the corner button
            </button>
          ) : null}
        </div>
      </div>
      <form onSubmit={add} className="flex flex-wrap items-center gap-1.5 px-4 pt-2">
        <Input className="min-w-40 flex-1 py-1 text-sm" value={text} placeholder={link ? `Add a to-do for this ${link.kind}…` : "Add a to-do…"} maxLength={300} onChange={(e) => setText(e.target.value)} />
        <Select className="w-36 py-1 text-xs" value={who || me} aria-label="For" onChange={(e) => setWho(e.target.value)}>
          {team.map((m) => (
            <option key={m.uid} value={m.uid}>
              {m.uid === me ? "Me" : m.name}
            </option>
          ))}
        </Select>
        <Input type="date" className="w-36 py-1 text-xs" value={due} aria-label="Due" onChange={(e) => setDue(e.target.value)} />
        <Button type="submit" className="px-2.5 py-1 text-xs" disabled={!text.trim()}>
          Add
        </Button>
      </form>
      {error ? <p className="px-4 pt-1 text-xs text-danger">{error}</p> : null}
      <div className="mt-2">
        {shown.slice(0, 50).map((r) => {
          const overdue = !r.done && r.dueAt !== null && r.dueAt < todayStart();
          const mayEdit = r.assignedUid === me || r.createdByUid === me || isManager;
          return (
            <div key={r.id} className="flex items-center gap-2 border-b border-steel/10 px-4 py-1.5 text-sm last:border-0">
              <input type="checkbox" checked={r.done} disabled={!mayEdit} onChange={() => toggle(r)} aria-label={r.done ? "Mark not done" : "Mark done"} />
              <span className={cn("min-w-0 flex-1 truncate", r.done ? "text-steel line-through" : "text-ink")}>
                {r.title}
                {r.link && !link ? (
                  <Link href={linkHref(r.link)} className="ml-1.5 text-xs text-brandorange-dark hover:underline">
                    {r.link.label}
                  </Link>
                ) : null}
              </span>
              {r.assignedUid !== me ? <span className="shrink-0 text-xs text-steel">{r.assignedName || nameOf(r.assignedUid)}</span> : null}
              {r.dueAt !== null && !r.done ? <span className={cn("shrink-0 text-xs", overdue ? "font-semibold text-danger" : "text-steel")}>{dueLabel(r.dueAt)}</span> : null}
              {r.createdByUid === me || isManager ? (
                <button className="shrink-0 text-steel/60 hover:text-danger" onClick={() => remove(r)} aria-label="Delete to-do">
                  <Trash2 size={13} />
                </button>
              ) : null}
            </div>
          );
        })}
        {shown.length === 0 ? <p className="px-4 pb-3 text-xs text-steel">{view === "mine" && !link ? "Nothing on your list." : "Nothing here."}</p> : null}
      </div>
    </Card>
  );
}
